/**
 * Item score system — every dropped item has a score 1..100 that scales its
 * stats. Score 60 is the neutral baseline: stats equal the raw def.stats.
 * Replaces the old discrete quality tiers (basic/uncommon/rare/legendary).
 *
 * Filename kept as "Quality.ts" to avoid churning ~15 import paths.
 */

/** A scored item instance — id into the def table, rolled score, generated name. */
export interface ScoredItem {
  id: string;
  score: number;
  name: string;
}

/**
 * Piecewise linear stat multiplier anchored at score=60 → 1.0.
 *   score 1   → 0.508
 *   score 60  → 1.0   (neutral baseline — equal to raw def.stats)
 *   score 100 → 1.8
 */
export function scoreMult(score: number): number {
  const s = Math.max(1, Math.min(100, score));
  if (s <= 60) return 0.5 + (s / 60) * 0.5;
  return 1.0 + ((s - 60) / 40) * 0.8;
}

/** UI band for coloring and labeling a score. */
export interface ScoreBand {
  label: string;
  color: number;
  min: number;
}

const BANDS: ScoreBand[] = [
  { label: 'Legendary', min: 96, color: 0xff4444 },
  { label: 'Epic',      min: 86, color: 0xaa55ff },
  { label: 'Rare',      min: 61, color: 0x4488ff },
  { label: 'Uncommon',  min: 31, color: 0x44cc44 },
  { label: 'Basic',     min: 1,  color: 0xaaaaaa },
];

export function scoreBand(score: number): ScoreBand {
  for (const b of BANDS) if (score >= b.min) return b;
  return BANDS[BANDS.length - 1]!;
}

export function scoreColor(score: number): number {
  return scoreBand(score).color;
}

/**
 * Roll a score based on distance from base.
 * Distance raises the ceiling; exponential skew makes high scores rare.
 * `bonus` (0..1, from loot-enhancer) flattens the skew, raising the mean.
 */
export function rollScore(distFromBase: number, bonus: number = 0): number {
  const maxScore = Math.max(40, Math.min(100, 40 + distFromBase * 0.35));
  const exp = Math.max(1.0, 2.5 - bonus);
  const t = Math.pow(Math.random(), exp);
  return Math.round(10 + t * (maxScore - 10));
}

/** Boss drops — high floor, flatter skew. */
export function rollBossScore(): number {
  const t = Math.pow(Math.random(), 1.3);
  return Math.round(55 + t * 45);
}

/** Price multiplier for trader stock/sell prices, anchored so score 60 ≈ 1.4×. */
export function priceMultFromScore(score: number): number {
  const s = Math.max(1, Math.min(100, score));
  return 0.5 + (s / 100) * 1.5;
}
